import {breakpoints} from "@next/themes"
import {useEffect, useRef} from "react"

interface StickyObserverOptions {
    ref: React.RefObject<HTMLElement>
    topOffset?: number // distance from top where sticky applies
    eventName?: string
    onChange?: (isStuck: boolean) => void
}

export default function useStickyObserver({ref, topOffset = 0, eventName, onChange}: StickyObserverOptions) {
    const sentinelRef = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        if (!ref.current) return

        const sentinel = document.createElement("div")
        sentinel.style.position = "absolute"
        sentinel.style.top = `${topOffset}rem`
        sentinel.style.left = `0rem`
        sentinel.style.height = "0.1px"
        sentinel.style.width = "100%"
        sentinel.style.maxWidth = `${breakpoints.values.xl}px`
        sentinel.style.pointerEvents = "none"

        // insert sentinel just before the sticky element
        ref.current.parentElement?.insertBefore(sentinel, ref.current)
        sentinelRef.current = sentinel

        const observer = new IntersectionObserver(
            ([entry]) => {
                const isStuck = entry.boundingClientRect.top < topOffset
                if (eventName) {
                    dispatchEvent(new CustomEvent(eventName, {detail: {showShadow: isStuck}}))
                }
                onChange?.(isStuck)
            },
            {
                root: null,
                threshold: [0],
            },
        )

        observer.observe(sentinel)

        return () => {
            observer.disconnect()
            sentinel.remove()
        }
    }, [ref, topOffset, eventName, onChange])
}
